/* eslint-disable max-len */
import { translate } from '../../../../common/language/translations';
import { StringToStringToStringMap } from '../../../types/common';
import { DataPosition, PositionInfo } from '../../../types/position';


const outfieldPositionKeys: DataPosition[] = ['S', 'LW', 'RW', 'CAM', 'CM', 'CDM', 'LB', 'RB', 'CB', 'F', 'M', 'D'];
const goalkeeperPositionKeys: DataPosition[] = ['GK', 'G'];


interface ReportVersionConfig {
  nameKey: string; // mandatory for tenant with multiple report versions
  positionKeys?: DataPosition[];
  isAcademyVersion?: boolean;
  scoreKeys: string[];
}


// overallScore is mandatory as the first score
// never remove versions, only add new ones
export const reportVersionToReportConfig: { [key: string]: ReportVersionConfig } = {

  // default version for clubs without custom versions defined
  '1.0.0': {
    nameKey: 'outfielder',
    positionKeys: outfieldPositionKeys,
    scoreKeys: [
      'overallScore',
      'potentialScore',
      'techniqueScore',
      'paceScore',
      'understandingScore',
      'strengthScore',
      'attitudeScore',
      'staminaScore',
      'influenceScore',
      'intensityScore',
    ],
  },
  '1.0.0_goalkeeper': {
    nameKey: 'goalkeeper',
    positionKeys: goalkeeperPositionKeys,
    scoreKeys: [
      'overallScore',
      'potentialScore',
      'reactionScore',
      'passingScore',
      'divingScore',
      'presenceScore',
      'positioningScore',
      'aggressionScore',
      'attitudeScore',
      'communicationScore',
    ],
  },
  '1.0.0_academy': {
    nameKey: 'academy',
    isAcademyVersion: true,
    scoreKeys: [
      'overallScore',
      'potentialScore',
      'techniqueScore',
      'physicalScore',
      'understandingScore',
      'maturityScore',
    ],
  },

  // rbk
  'rbk-1.0.0': {
    nameKey: 'outfielder',
    positionKeys: outfieldPositionKeys,
    scoreKeys: [
      'overallScore',
      'potentialScore',
      'roleScore',
      'influenceScore',
      'techniqueScore',
      'tacticalScore',
      'physicalScore',
      'mentalScore',
      'attackingScore',
      'defendingScore',
    ],
  },

  // vif
  'vif-1.0.0': {
    nameKey: 'newVersion',
    scoreKeys: [
      'overallScore',
      'potentialScore',
      'xFactorScore',
    ],
  },

  // bryne
  'bryne-1.0.0': {
    nameKey: 'outfielder',
    positionKeys: outfieldPositionKeys,
    scoreKeys: [
      'overallScore',
      'potentialScore',
      'attitudeScore',
      'boldnessScore',
      'paceScore',
      'duelStrengthScore',
      'techniqueScore',
      'xFactorScore',
      'understandingScore',
      'adaptabilityScore',
    ],
  },

  // agf
  'agf-1.0.0_striker': {
    nameKey: 'striker',
    positionKeys: ['S'],
    scoreKeys: [
      'overallScore',
      'potentialScore',
      'aggressionScore',
      'dynamicScore',
      'forwardMindedScore',
      'accelerationScore',
      'topSpeedScore',
      'goalContributionScore',
      'finishingScore',
      'deepRunningScore',
      'aerialScore',
      'holdUpScore',
      '1v1OffensiveScore',
      'linkUpScore',
      'boxPresenceScore',
      'intensityScore',
      'pressingScore',
      'workRateScore',
      'offensiveSetPieceScore',
      'defensiveSetPieceScore',
    ],
  },
  'agf-1.0.0_offensive': {
    nameKey: 'offensive',
    positionKeys: ['LW', 'RW', 'CAM', 'F'],
    scoreKeys: [
      'overallScore',
      'potentialScore',
      'aggressionScore',
      'dynamicScore',
      'forwardMindedScore',
      'accelerationScore',
      'topSpeedScore',
      '1v1OffensiveScore',
      'deepRunningScore',
      'creativityScore',
      'receivalScore',
      'finishingScore',
      'goalContributionScore',
      'pressingScore',
      'workRateScore',
      'intensityScore',
      'setPieceScore',
    ],
  },
  'agf-1.0.0_midfielder': {
    nameKey: 'midfielder',
    positionKeys: ['CM', 'M'],
    scoreKeys: [
      'overallScore',
      'potentialScore',
      'aggressionScore',
      'dynamicScore',
      'forwardMindedScore',
      'accelerationScore',
      'passingScore',
      'orientationScore',
      'composureScore',
      'boxEntryScore',
      'deepRunningScore',
      'goalContributionScore',
      'duellingScore',
      'pressingScore',
      'workRateScore',
      'intensityScore',
      'setPieceScore',
    ],
  },
  'agf-1.0.0_defensiveMidfielder': {
    nameKey: 'defensiveMidfielder',
    positionKeys: ['CDM'],
    scoreKeys: [
      'overallScore',
      'potentialScore',
      'aggressionScore',
      'dynamicScore',
      'forwardMindedScore',
      'accelerationScore',
      'passingScore',
      'orientationScore',
      'composureScore',
      'defensivePositioningScore',
      'interceptionScore',
      'duellingScore',
      'setPieceScore',
    ],
  },
  'agf-1.0.0_fullBack': {
    nameKey: 'fullBack',
    positionKeys: ['LB', 'RB'],
    scoreKeys: [
      'overallScore',
      'potentialScore',
      'aggressionScore',
      'dynamicScore',
      'forwardMindedScore',
      'accelerationScore',
      'topSpeedScore',
      'goalContributionScore',
      'buildUpScore',
      '1v1OffensiveScore',
      'deepRunningScore',
      'combinationScore',
      'crossingScore',
      'boxDefendingScore',
      'defensiveUnderstandingScore',
      '1v1DefensiveScore',
      'intensityScore',
      'setPieceScore',
    ],
  },
  'agf-1.0.0_centerBack': {
    nameKey: 'centerBack',
    positionKeys: ['CB', 'D'],
    scoreKeys: [
      'overallScore',
      'potentialScore',
      'aggressionScore',
      'dynamicScore',
      'forwardMindedScore',
      'accelerationScore',
      'topSpeedScore',
      'aerialScore',
      'forwardDefendingScore',
      'defensiveUnderstandingScore',
      '1v1DefensiveScore',
      'duelStrengthScore',
      'boxDefendingScore',
      'carryScore',
      'passingScore',
      'longPassingScore',
      'offensiveSetPieceScore',
      'defensiveSetPieceScore',
    ],
  },
  'agf-1.0.0_goalkeeper': {
    nameKey: 'goalkeeper',
    positionKeys: ['GK', 'G'],
    scoreKeys: [
      'overallScore',
      'potentialScore',
      'aggressionScore',
      'dynamicScore',
      'forwardMindedScore',
    ],
  },

  // fokus
  'fokusbronze-1.0.0': {
    nameKey: 'newVersion',
    scoreKeys: [
      'overallScore',
      'potentialScore',
      'xFactorScore',
    ],
  },
  'fokus-1.0.0_attackers': {
    nameKey: 'attacker',
    positionKeys: ['S', 'LW', 'RW', 'CAM', 'F'],
    scoreKeys: [
      'overallScore',
      'potentialScore',
      'attackingScore',
      'finishingScore',
    ],
  },
  'fokus-1.0.0_midfielders': {
    nameKey: 'midfielder',
    positionKeys: ['CM', 'CDM', 'M'],
    scoreKeys: [
      'overallScore',
      'potentialScore',
      'tacticalScore',
      'passingScore',
    ],
  },
  'fokus-1.0.0_defenders': {
    nameKey: 'defender',
    positionKeys: ['LB', 'RB', 'CB', 'D'],
    scoreKeys: [
      'overallScore',
      'potentialScore',
      'defendingScore',
      'duelStrengthScore',
    ],
  },
  'fokus-1.0.0_goalkeepers': {
    nameKey: 'goalkeeper',
    positionKeys: ['GK', 'G'],
    scoreKeys: [
      'overallScore',
      'potentialScore',
      'reactionScore',
      'positioningScore',
    ],
  },
};


// versions should be ordered such that more narrow cases come last - if no position is provided, the first version will be used
export const getLatestReportVersions = (tenant: string | undefined): string[] => {
  switch (tenant) {
    // club specific versions
    case 'rbk':
      return [
        'rbk-1.0.0',
        '1.0.0_goalkeeper',
      ];

    case 'vif':
      return [
        'vif-1.0.0'
      ];

    case 'bryne':
      return [
        'bryne-1.0.0',
        '1.0.0_goalkeeper',
      ];

    case 'agf':
      return [
        'agf-1.0.0_striker',
        'agf-1.0.0_offensive',
        'agf-1.0.0_midfielder',
        'agf-1.0.0_defensiveMidfielder',
        'agf-1.0.0_fullBack',
        'agf-1.0.0_centerBack',
        'agf-1.0.0_goalkeeper',
      ];

    // fokus versions
    case 'fokusbronze':
      return [
        'fokusbronze-1.0.0'
      ];

    case 'fokussilver':
      return [
        'agf-1.0.0_striker',
        'agf-1.0.0_offensive',
        'agf-1.0.0_midfielder',
        'agf-1.0.0_defensiveMidfielder',
        'agf-1.0.0_fullBack',
        'agf-1.0.0_centerBack',
        'agf-1.0.0_goalkeeper',
      ];

    case 'fokus':
      return [
        'fokus-1.0.0_attackers',
        'fokus-1.0.0_midfielders',
        'fokus-1.0.0_defenders',
        'fokus-1.0.0_goalkeepers',
      ];

    case 'demo':
      return [
        '1.0.0',
        '1.0.0_goalkeeper',
        '1.0.0_academy',
      ];

    // default
    default:
      return [
        '1.0.0',
        '1.0.0_goalkeeper',
      ];
  }
};


// try to find the most appropriate version based on the position
export const getInitialReportVersion = (
  tenant: string | undefined,
  positionInfo: PositionInfo | undefined,
  isAcademyPlayer: boolean | undefined | null,
): string => {

  const latestReportVersions = getLatestReportVersions(tenant);

  latestReportVersions.reverse(); // reverse to check more narrow cases first

  // if only one version, return it
  if (latestReportVersions.length === 1) {
    return latestReportVersions[0];
  }

  if (isAcademyPlayer) {
    const academyVersions = latestReportVersions.filter(version => reportVersionToReportConfig[version]?.isAcademyVersion);
    if (academyVersions.length > 0) {
      return getBestVersionForPosition(positionInfo, academyVersions);
    }
  }

  return getBestVersionForPosition(positionInfo, latestReportVersions);
};


const getBestVersionForPosition = (
  positionInfo: PositionInfo | undefined,
  reportVersions: string[],
): string => {

  // if no position info, return the last version
  if (!positionInfo) {
    return reportVersions[reportVersions.length - 1];
  }

  // return the first config containing any of the primary positions, if any
  for (const version of reportVersions) {
    const versionConfig = reportVersionToReportConfig[version];
    if (versionConfig?.positionKeys?.some(position => positionInfo.primary_positions.includes(position))) {
      return version;
    }
  }

  // return the first config containing any of the secondary positions, if any
  for (const version of reportVersions) {
    const versionConfig = reportVersionToReportConfig[version];
    if (versionConfig?.positionKeys?.some(position => positionInfo.secondary_positions.includes(position))) {
      return version;
    }
  }

  // return the first version containing any of the positions, if any
  const allPositionsPlayed = positionInfo.positions.map(position => position.position);
  for (const version of reportVersions) {
    const versionConfig = reportVersionToReportConfig[version];
    if (versionConfig?.positionKeys?.some(position => allPositionsPlayed.includes(position))) {
      return version;
    }
  }

  // return the last version
  return reportVersions[reportVersions.length - 1];
};


export const getReportVersionDisplayName = (version: string, language: string | undefined, tenant: string | undefined): string => {
  const nameKey = reportVersionToReportConfig[version]?.nameKey;

  const latestReportVersions = getLatestReportVersions(tenant);
  const isLatestVersion = latestReportVersions.includes(version);

  let displayName = nameKey ? reportNameLanguageMap[nameKey]?.[language ?? 'en'] : version;
  if (!isLatestVersion) {
    displayName += ' (' + translate('old', language, true) + ')';
  }

  return displayName;
};


export const getCommonScoresForAllReportVersions = (tenant: string | undefined): string[] => {

  const latestReportVersions = getLatestReportVersions(tenant);

  if (latestReportVersions.length === 1) {
    return reportVersionToReportConfig[latestReportVersions[0]]?.scoreKeys ?? [];
  }

  const commonScoreKeys: string[] = [];

  const firstVersionConfig = reportVersionToReportConfig[latestReportVersions[0]];
  for (const scoreKey of firstVersionConfig?.scoreKeys ?? []) {

    let scoreKeyIsCommon = true;
    for (let i = 1; i < latestReportVersions.length; i++) {
      const versionConfig = reportVersionToReportConfig[latestReportVersions[i]];
      if (!versionConfig?.scoreKeys?.includes(scoreKey)) {
        scoreKeyIsCommon = false;
        break;
      }
    }

    if (scoreKeyIsCommon) {
      commonScoreKeys.push(scoreKey);
    }
  }

  return commonScoreKeys;
};


export const reportNameLanguageMap: StringToStringToStringMap = {
  'newVersion': {
    'en': 'New version',
    'no': 'Ny versjon',
    'se': 'Ny version',
    'dk': 'Ny version',
  },
  'outfielder': {
    'en': 'Outfielder',
    'no': 'Utespiller',
    'se': 'Utspelare',
    'dk': 'Markspiller',
  },
  'attacker': {
    'en': 'Attacker',
    'no': 'Angriper',
    'se': 'Angripare',
    'dk': 'Angriber',
  },
  'striker': {
    'en': 'Striker',
    'no': 'Spiss',
    'se': 'Anfallare',
    'dk': 'Angriber',
  },
  'offensive': {
    'en': 'Offensive',
    'no': 'Offensiv',
    'se': 'Offensiv',
    'dk': 'Offensiv',
  },
  'midfielder': {
    'en': 'Midfielder',
    'no': 'Midtbane',
    'se': 'Mittfältare',
    'dk': 'Midtbane',
  },
  'defensiveMidfielder': {
    'en': 'Defensive midfielder',
    'no': 'Defensiv midtbane',
    'se': 'Defensiv mittfältare',
    'dk': 'Defensiv midtbane',
  },
  'defender': {
    'en': 'Defender',
    'no': 'Forsvarer',
    'se': 'Försvarare',
    'dk': 'Forsvarer',
  },
  'fullBack': {
    'en': 'Full back',
    'no': 'Sideback',
    'se': 'Sideback',
    'dk': 'Sideback',
  },
  // 'wingBack': {
  //   'en': 'Wing back',
  //   'no': 'Vingback',
  //   'se': 'Wingback',
  //   'dk': 'Wing back',
  // },
  'centerBack': {
    'en': 'Center back',
    'no': 'Midtstopper',
    'se': 'Mittback',
    'dk': 'Midterforsvarer',
  },
  // 'wideCenterBack': {
  //   'en': 'Wide center back',
  //   'no': 'Sidestopper',
  //   'se': 'Yttermittback',
  //   'dk': 'Yderste midterforsvarer',
  // },
  'goalkeeper': {
    'en': 'Goalkeeper',
    'no': 'Keeper',
    'se': 'Målvakt',
    'dk': 'Målmand',
  },
  'academy': {
    'en': 'Academy',
    'no': 'Akademi',
    'se': 'Akademi',
    'dk': 'Akademi',
  },
};


export const getScoreTitleFontSize = (
  scoreTitle: string,
  defaultFontSize: number = 14,
  minFontSize: number = 11,
): number => {
  if (scoreTitle.length > 20) {
    return Math.max(defaultFontSize - 3, minFontSize);
  }

  if (scoreTitle.length > 18) {
    return Math.max(defaultFontSize - 2, minFontSize);
  }

  if (scoreTitle.length > 16) {
    return Math.max(defaultFontSize - 1, minFontSize);
  }

  return defaultFontSize;
};


export const getReportScoreName = (
  scoreKey: string,
  language: string | undefined,
  tenant: string | undefined,
): string | undefined => {
  const scoreConfig = reportScoreToReportScoreConfig[scoreKey];

  if (tenant && scoreConfig?.name[tenant]) {
    return scoreConfig?.name[tenant][language ?? 'en'];
  }

  if (scoreConfig?.name?.default) {
    return scoreConfig?.name.default[language ?? 'en'];
  }

  return undefined;
};


export const getReportScoreDescription = (
  scoreKey: string,
  language: string | undefined,
  tenant: string | undefined,
): string | undefined => {
  const scoreConfig = reportScoreToReportScoreConfig[scoreKey];

  if (tenant && scoreConfig?.description?.[tenant]) {
    return scoreConfig?.description[tenant][language ?? 'en'];
  }

  if (scoreConfig?.description?.default) {
    return scoreConfig?.description.default[language ?? 'en'];
  }

  return undefined;
};


interface ReportScoreConfig {
  name: StringToStringToStringMap; // tenant to language to name
  description?: StringToStringToStringMap; // tenant to language to description
}

export const reportScoreToReportScoreConfig: { [key: string]: ReportScoreConfig } = {

  // general
  'overallScore': {
    name: {
      'default': {
        'en': 'Overall',
        'no': 'Overordnet',
        'se': 'Övergripande',
        'dk': 'Samlet',
      },
    },
    description: {
      'agf': {
        'en': 'Expected current Superliga level.',
        'no': 'Forventet nåværende Superliga-nivå.',
        'se': 'Förväntad nuvarande Superliga-nivå.',
        'dk': 'Forventet nuværende Superliga-niveau.',
      },
    },
  },

  'potentialScore': {
    name: {
      'default': {
        'en': 'Potential',
        'no': 'Potensial',
        'se': 'Potential',
        'dk': 'Potentiale',
      },
    },
    description: {
      'agf': {
        'en': 'Expected potential Superliga level.',
        'no': 'Forventet potensielt Superliga-nivå.',
        'se': 'Förväntad potentiell Superliga-nivå.',
        'dk': 'Forventet potentielt Superliga-niveau.',
      },
    },
  },

  'influenceScore': {
    name: {
      'default': {
        'en': 'Influence',
        'no': 'Innflytelse',
        'se': 'Inflytande',
        'dk': 'Indflydelse',
      },
    },
  },

  'presenceScore': {
    name: {
      'default': {
        'en': 'Presence',
        'no': 'Tilstedeværelse',
        'se': 'Närvaro',
        'dk': 'Tilstedeværelse',
      },
    },
  },

  'communicationScore': {
    name: {
      'default': {
        'en': 'Communication',
        'no': 'Kommunikasjon',
        'se': 'Kommunikation',
        'dk': 'Kommunikation',
      }
    },
  },

  'roleScore': {
    name: {
      'default': {
        'en': 'Role skills',
        'no': 'Rolleferdigheter',
        'se': 'Rollfärdigheter',
        'dk': 'Rollefærdigheder',
      },
    },
  },

  'xFactorScore': {
    name: {
      'default': {
        'en': 'X-factor',
        'no': 'X-faktor',
        'se': 'X-faktor',
        'dk': 'X-faktor',
      },
    },
  },

  'combinationScore': {
    name: {
      'default': {
        'en': 'Combination play',
        'no': 'Kombinasjonsspill',
        'se': 'Kombinationsspel',
        'dk': 'Kombinationsspil',
      },
    },
    description: {
      'agf': {
        'en': 'Relationel ability. Offering himself in good positions, find teammates in good positions. Actively using teammates to advance up the pitch. Give and go\'s.',
        'no': 'Relasjonelle ferdigheter. Tilbyr seg i gode posisjoner, finner medspillere i gode rom. Bruker aktivt medspillere for å flytte spillet fremover. Veggspill og kombinasjoner.',
        'se': 'Relationella färdigheter. Erbjuder sig i bra positioner, hittar medspelare i gynnsamma ytor. Använder lagkamrater aktivt för att ta spelet framåt. Väggspel och kombinationer.',
        'dk': 'Relationelle evner. Tilbyder sig i gode positioner, finder medspillere i fordelagtige rum. Bruger aktivt medspillere til at spille sig fremad. Vægspil og kombinationer.',
      },
    },
  },

  'creativityScore': {
    name: {
      'default': {
        'en': 'Creativity',
        'no': 'Kreativitet',
        'se': 'Kreativitet',
        'dk': 'Kreativitet',
      },
    },
    description: {
      'agf': {
        'en': 'Ability to unlock well organized defences with vision and creative passing.',
        'no': 'Evne til å bryte ned godt organiserte forsvar med blikk og kreative pasninger.',
        'se': 'Förmåga att bryta ner välorganiserade försvar med speluppfattning och kreativa passningar.',
        'dk': 'Evne til at åbne velorganiserede forsvar med overblik og kreative afleveringer.',
      },
    },
  },

  'orientationScore': {
    name: {
      'default': {
        'en': 'Orientation',
        'no': 'Orientering',
        'se': 'Orientering',
        'dk': 'Orientering',
      },
    },
    description: {
      'agf': {
        'en': 'Preparation before receiving. Does he know what is going on around him and have the ability to execute his next move.',
        'no': 'Forberedelse før mottak. Vet han hva som skjer rundt seg og har han evne til å utføre neste aksjon.',
        'se': 'Förberedelse före mottag. Vet han vad som händer runt omkring och har han förmågan att genomföra nästa aktion.',
        'dk': 'Forberedelse før modtagelse. Ved han, hvad der sker omkring ham, og har han evnen til at udføre næste aktion.',
      },
    },
  },


  // technical
  'techniqueScore': {
    name: {
      'default': {
        'en': 'Technique',
        'no': 'Teknikk',
        'se': 'Teknik',
        'dk': 'Teknik',
      },
    },
  },

  'carryScore': {
    name: {
      'default': {
        'en': 'Ball carrying',
        'no': 'Ballføring',
        'se': 'Bollföring',
        'dk': 'Boldføring',
      },
    },
    description: {
      'agf': {
        'en': 'Ability to either carry or dribble the ball out of pressure.',
        'no': 'Evne til å føre eller drible ballen ut av press.',
        'se': 'Förmåga att föra eller dribbla sig ur press.',
        'dk': 'Evne til at føre eller drible sig ud af pres.',
      },
    },
  },

  'receivalScore': {
    name: {
      'default': {
        'en': 'Receival',
        'no': 'Mottak',
        'se': 'Mottagning',
        'dk': 'Modtagning',
      },
    },
    description: {
      'agf': {
        'en': 'Abilty to find open spaces between opponent\'s first and second line, either central or half space.',
        'no': 'Evne til å finne rom mellom motstanderens første og andre linje, enten sentralt eller i mellomrommet.',
        'se': 'Förmåga att hitta ytor mellan motståndarens första och andra linje, centralt eller i halvytan.',
        'dk': 'Evne til at finde rum mellem modstanderens første og anden linje - centralt eller i halvrummet.',
      },
    },
  },


  // passing
  'passingScore': {
    name: {
      'default': {
        'en': 'Passing',
        'no': 'Pasninger',
        'se': 'Passningar',
        'dk': 'Afleveringer',
      },
    },
    description: {
      'agf': {
        'en': 'Ability to play through opponents organizational structure.',
        'no': 'Evne til å spille gjennom motstanderens strukturerte forsvar.',
        'se': 'Förmåga att spela sig genom motståndarens försvarsorganisation.',
        'dk': 'Evne til at spille gennem modstanderens defensive struktur.',
      },
    },
  },

  'longPassingScore': {
    name: {
      'default': {
        'en': 'Long passing',
        'no': 'Langpasninger',
        'se': 'Långpassningar',
        'dk': 'Langafleveringer',
      },
    },
    description: {
      'agf': {
        'en': 'Longer passes, most likely diagonals or aerial passes in behind.',
        'no': 'Lengre pasninger, ofte diagonale eller løftede pasninger i bakrom.',
        'se': 'Längre passningar, oftast diagonala eller lyfta bollar bakom backlinjen.',
        'dk': 'Længere afleveringer, typisk diagonale eller høje bolde i dybden.',
      },
    },
  },

  'crossingScore': {
    name: {
      'default': {
        'en': 'Crossing',
        'no': 'Innlegg',
        'se': 'Inlägg',
        'dk': 'Indlæg',
      },
    },
    description: {
      'agf': {
        'en': 'Crossing ability. Early crosses between backline and goalkeeper, chipped passes aiming far post, cutbacks etc.',
        'no': 'Innleggskvalitet. Tidlige innlegg mellom forsvar og keeper, chipper mot bakerste stolpe, 45-graderspasninger osv.',
        'se': 'Inläggsförmåga. Tidiga inlägg mellan backlinje och målvakt, chippar mot bortre stolpen, snett-inåt-bakåt etc.',
        'dk': 'Indlægsstyrke. Tidlige indlæg mellem forsvar og målmand, chip mod bagerste stolpe, cutbacks osv.',
      },
    },
  },


  // understanding
  'understandingScore': {
    name: {
      'default': {
        'en': 'Understanding',
        'no': 'Spilleforståelse',
        'se': 'Spelförståelse',
        'dk': 'Spilleforståelse',
      },
    },
  },

  'positioningScore': {
    name: {
      'default': {
        'en': 'Positioning',
        'no': 'Posisjonering',
        'se': 'Positionering',
        'dk': 'Positionering',
      },
    },
  },

  'tacticalScore': {
    name: {
      'default': {
        'en': 'Tactical',
        'no': 'Taktikk',
        'se': 'Taktik',
        'dk': 'Taktik',
      },
    },
  },


  // mental
  'mentalScore': {
    name: {
      'default': {
        'en': 'Mentality',
        'no': 'Mentalitet',
        'se': 'Mentalitet',
        'dk': 'Mentalitet',
      },
    },
  },

  'attitudeScore': {
    name: {
      'default': {
        'en': 'Attitude',
        'no': 'Holdning',
        'se': 'Attityd',
        'dk': 'Holdning',
      },
    },
  },

  'maturityScore': {
    name: {
      'default': {
        'en': 'Maturity',
        'no': 'Modenhet',
        'se': 'Mognad',
        'dk': 'Modenhed',
      },
    },
  },

  'boldnessScore': {
    name: {
      'default': {
        'en': 'Boldness',
        'no': 'Modighet',
        'se': 'Modighet',
        'dk': 'Modighet',
      },
    },
  },

  'adaptabilityScore': {
    name: {
      'default': {
        'en': 'Adaptability',
        'no': 'Omstilling',
        'se': 'Omställning',
        'dk': 'Omstilling',
      },
    },
  },

  'aggressionScore': {
    name: {
      'agf': {
        'en': 'Aggressive',
        'no': 'Aggressiv',
        'se': 'Aggressiv',
        'dk': 'Aggressiv',
      },
      'default': {
        'en': 'Aggression',
        'no': 'Aggressivitet',
        'se': 'Aggressivitet',
        'dk': 'Aggressivitet',
      },
    },
    description: {
      'agf': {
        'en': 'The player is actively looking for duels, chasing opponents and wants to attack the ball.\nThe player is highly competitive and expresses his desire to win.\nThe player is ready to go beyond his physical capacities to win.\nThe player is liking a fight and willing to hurt the opponent in the boundaries of the rules.',
        'no': 'Spilleren er aktivt ute etter dueller, jager motstandere og ønsker å angripe ballen.\nSpilleren er svært konkurranseinnstilt og uttrykker sitt ønske om å vinne.\nSpilleren er klar til å gå utover sine fysiske evner for å vinne.\nSpilleren liker kamp og er villig til å skade motstanderen innenfor regelverkets grenser.',
        'se': 'Spelaren söker aktivt dueller, jagar motståndare och vill attackera bollen.\nSpelaren är mycket tävlingsinriktad och uttrycker sin önskan att vinna.\nSpelaren är redo att gå utöver sina fysiska förmågor för att vinna.\nSpelaren tycker om att slåss och är villig att skada sin motståndare inom reglernas gränser.',
        'dk': 'Spilleren søger aktivt dueller, jagter modstandere og ønsker at angribe bolden.\nSpilleren er meget konkurrenceminded og udtrykker sit ønske om at vinde.\nSpilleren er klar til at gå ud over sine fysiske evner for at vinde.\nSpilleren nyder at kæmpe og er villig til at skade sin modstander inden for reglernes rammer.',
      },
    },
  },

  'dynamicScore': {
    name: {
      'default': {
        'en': 'Dynamic',
        'no': 'Dynamisk',
        'se': 'Dynamisk',
        'dk': 'Dynamisk',
      },
    },
    description: {
      'agf': {
        'en': 'The player is physically fast and can beat opponents in running duels and on transitions.\nThe player is explosive in his actions, being able to accelerate and decelerate with high intensity.\nThe player is repeatedly fast and explosive, able to recover quickly and perform numerous high-intensity actions.\nThe player is enduring physically and mentally, therefore keeping up the game tempo in every phase of the game.',
        'no': 'Spilleren er fysisk rask og kan slå motstandere i løpsdueller og i overganger.\nSpilleren er eksplosiv i sine handlinger, og kan akselerere og bremse med høy intensitet.\nSpilleren er gjentatte ganger rask og eksplosiv, i stand til å hente seg inn raskt og utføre en rekke høyintensitetshandlinger.\nSpilleren er utholdende fysisk og mentalt, og holder derfor spilletempoet oppe i hver fase av spillet.',
        'se': 'Spelaren är fysiskt snabb och kan besegra motståndare i löpdueller och vid övergångar.\nSpelaren är explosiv i sina handlingar och kan accelerera och bromsa med hög intensitet.\nSpelaren är upprepade gånger snabb och explosiv, kan återhämta sig snabbt och utföra många högintensiva handlingar.\nSpelaren är uthållig fysiskt och mentalt och håller därför uppe speltempot i varje fas av spelet.',
        'dk': 'Spilleren er fysisk hurtig og kan slå modstandere i løbedueller og i overgange.\nSpilleren er eksplosiv i sine handlinger og er i stand til at accelerere og decelerere med høj intensitet.\nSpilleren er gentagne gange hurtig og eksplosiv, i stand til at restituere hurtigt og udføre adskillige højintensitetshandlinger.\nSpilleren er udholdende fysisk og mentalt og holder derfor spilletempoet oppe i alle spillets faser.',
      },
    },
  },

  'forwardMindedScore': {
    name: {
      'default': {
        'en': 'Forward-minded',
        'no': 'Framovertenkt',
        'se': 'Framåtsinnad',
        'dk': 'Fremadrettet',
      },
    },
    description: {
      'agf': {
        'en': 'The player is always scanning the pitch and positions correctly, wanting to take the first touch forward and breaking lines.\nThe player wants to play forward and keeps moving with the intention to join attacks and score goals.\nThe player wants to beat his opponent in 1v1 situations by dribbling or combination play.\nThe player has a high speed of execution and strong decision-making skills, seeing chances instead of avoiding risks, with reference to his position on the pitch.',
        'no': 'Spilleren skanner alltid banen og posisjonerer seg riktig, og ønsker å ta førsteberøringen fremover og bryte linjer.\nSpilleren ønsker å spille fremover og beveger seg med hensikt om å bli med i angrep og score mål.\nSpilleren ønsker å slå motstanderen i 1v1-situasjoner ved å drible eller ved kombinasjonsspill.\nSpilleren har høy utførelseshastighet og sterke beslutningstakingferdigheter, og ser sjanser i stedet for å unngå risikoer, med referanse til sin posisjon på banen.',
        'se': 'Spelaren skannar alltid planen och positionerar sig korrekt, med en önskan att ta första touchen framåt och bryta linjer.\nSpelaren vill spela framåt och rör sig med avsikt att ansluta sig till anfall och göra mål.\nSpelaren vill besegra motståndaren i 1v1-situationer genom dribbling eller kombinationsspel.\nSpelaren har hög utförandehastighet och stark beslutsfattande förmåga, och ser chanser snarare än att undvika risker, med hänvisning till sin position på planen.',
        'dk': 'Spilleren scanner altid banen og positionerer sig korrekt, med det formål at tage førsteberøring fremad og bryde linjer.\nSpilleren ønsker at spille fremad og bevæger sig med den hensigt at deltage i angreb og score mål.\nSpilleren ønsker at slå modstanderen i 1v1-situationer ved at drible eller kombinere spil.\nSpilleren har høj udførelseshastighed og stærke beslutningsevner og ser chancer snarere end at undgå risici, med henvisning til sin position på banen.',
      },
    },
  },

  'composureScore': {
    name: {
      'default': {
        'en': 'Composure',
        'no': 'Ro',
        'se': 'Lugn',
        'dk': 'Ro',
      },
    },
    description: {
      'agf': {
        'en': 'Behaviour when facing pressure. Keeping composure, or playing long balls or backward passes.',
        'no': 'Adferd under press. Beholder roen, eller spiller langt eller bakover.',
        'se': 'Beteende under press.ehåller lugnet, eller spelar långt eller bakåt.',
        'dk': 'Adfærd under pres. Bevarer roen eller spiller langt eller bagud.',
      },
    },
  },


  // physical
  'physicalScore': {
    name: {
      'default': {
        'en': 'Physical',
        'no': 'Fysikk',
        'se': 'Fysik',
        'dk': 'Fysik',
      },
    },
  },

  'strengthScore': {
    name: {
      'default': {
        'en': 'Strength',
        'no': 'Styrke',
        'se': 'Styrka',
        'dk': 'Styrke',
      },
    },
  },

  'paceScore': {
    name: {
      'default': {
        'en': 'Pace',
        'no': 'Hurtighet',
        'se': 'Fart',
        'dk': 'Hurtighed',
      },
    },
  },

  'accelerationScore': {
    name: {
      'default': {
        'en': 'Acceleration',
        'no': 'Akselerasjon',
        'se': 'Acceleration',
        'dk': 'Acceleration',
      },
    },
    description: {
      'agf': {
        'en': 'Explosivity to acceletate or decelerate speed.',
        'no': 'Eksplosivitet til å akselerere eller bremse hastighet.',
        'se': 'Explosivitet för att accelerera eller bromsa hastigheten.',
        'dk': 'Eksplosivitet til at accelerere eller decelerere hastigheden.',
      },
    },
  },

  'topSpeedScore': {
    name: {
      'default': {
        'en': 'Top speed',
        'no': 'Toppfart',
        'se': 'Toppfart',
        'dk': 'Topfart',
      },
    },
    description: {
      'agf': {
        'en': 'Maximum speed on longer distances.',
        'no': 'Maksimal hastighet på lengre distanser.',
        'se': 'Maximal hastighet på längre distanser.',
        'dk': 'Maksimal hastighed på længere distancer.',
      },
    },
  },

  'staminaScore': {
    name: {
      'default': {
        'en': 'Stamina',
        'no': 'Utholdenhet',
        'se': 'Uthållighet',
        'dk': 'Udholdenhed',
      },
    },
  },

  'intensityScore': {
    name: {
      'default': {
        'en': 'Intensity',
        'no': 'Intensitet',
        'se': 'Intensitet',
        'dk': 'Intensitet',
      },
    },
    description: {
      'agf': {
        'en': 'Generel intensity. Number and quality of high intensity actions.',
        'no': 'Generell intensitet. Antall og kvalitet på høyintensive aksjoner.',
        'se': 'Allmän intensitet. Antal och kvalitet på högintensiva aktioner.',
        'dk': 'Generel intensitet. Antal og kvalitet af højintense aktioner.',
      },
    },
  },

  'pressingScore': {
    name: {
      'default': {
        'en': 'Pressing',
        'no': 'Presspill',
        'se': 'Presspel',
        'dk': 'Presspil',
      },
    },
    description: {
      'agf': {
        'en': 'Effective pressing. Recognize press-triggers and pressing intensively to stress opponent\'s possession.',
        'no': 'Effektivt press. Gjenkjenner presstriggere og presser intenst for å stresse motstanderens ballbesittelse.',
        'se': 'Effektiv press. Känner igen presstriggers och pressar intensivt för att störa motståndarens bollinnehav.',
        'dk': 'Effektivt pres. Genkender pres-triggere og presser intenst for at stresse modstanderens boldbesiddelse.',
      },
    },
  },

  'workRateScore': {
    name: {
      'default': {
        'en': 'Work rate',
        'no': 'Arbeidskapasitet',
        'se': 'Arbetskapacitet',
        'dk': 'Arbejdsrate',
      },
    },
    description: {
      'agf': {
        'en': 'Capable to run for full 90 minutes, not dropping his physical output drastically towards the end of the game.',
        'no': 'Klarer å løpe i 90 minutter uten å falle mye i fysisk kapasitet mot slutten av kampen.',
        'se': 'Klarar att springa i 90 minuter utan att tappa nämnvärt i fysisk prestation mot slutet av matchen.',
        'dk': 'Kan løbe i 90 minutter uden markant fald i fysisk præstation mod kampens slutning.',
      },
    },
  },


  // attacking
  'attackingScore': {
    name: {
      'default': {
        'en': 'Attacking',
        'no': 'Angrep',
        'se': 'Anfall',
        'dk': 'Angreb',
      },
    },
  },

  '1v1OffensiveScore': {
    name: {
      'default': {
        'en': 'Offensive 1v1',
        'no': 'Offensiv 1v1',
        'se': 'Offensiv 1v1',
        'dk': 'Offensiv 1v1',
      },
    },
    description: {
      'agf': {
        'en': 'Take-ons. Facing an opponent and manages to dribble past him with individual skills.',
        'no': 'Driblinger. Tar seg forbi motspiller én mot én med individuelle ferdigheter.',
        'se': 'Dribblingar. Tar sig förbi motståndare en mot en med individuella färdigheter.',
        'dk': 'Driblinger. Går forbi en modstander én mod én med individuelle færdigheder.',
      },
    },
  },

  'goalContributionScore': {
    name: {
      'default': {
        'en': 'Goal contribution',
        'no': 'Målbidrag',
        'se': 'Målbidrag',
        'dk': 'Målbidrag',
      },
    },
    description: {
      'agf': {
        'en': 'Involved in goals, either scoring, assisting or 2nd assists.',
        'no': 'Involvert i mål, enten scoring, assist eller 2. assist.',
        'se': 'Involverad i mål, antingen genom scoring, assist eller 2. assist.',
        'dk': 'Involveret i mål, enten scoring, assist eller 2. assist.',
      },
    },
  },

  'finishingScore': {
    name: {
      'default': {
        'en': 'Finishing',
        'no': 'Avslutning',
        'se': 'Avslut',
        'dk': 'Afslutning',
      },
    },
    description: {
      'agf': {
        'en': 'Quality of finishing. Likelihood of outperforming xG due to quality finishing.',
        'no': 'Avslutningskvalitet. Sannsynlighet for å overprestere xG grunnet høy avslutningskvalitet.',
        'se': 'Avslutskvalitet. Sannolikhet att överprestera xG tack vare hög avslutskvalitet.',
        'dk': 'Afslutningskvalitet. Sandsynlighed for at overpræstere xG på grund af høj afslutningskvalitet.',
      },
    },
  },

  'deepRunningScore': {
    name: {
      'default': {
        'en': 'Deep running',
        'no': 'Dype løp',
        'se': 'Djupledslöpning',
        'dk': 'Dybdeløb',
      },
    },
    description: {
      'agf': {
        'en': 'Offering himself as a passing option behind the defensive line.',
        'no': 'Tilbyr seg som pasningsalternativ i bakrommet.',
        'se': 'Erbjuder sig som ett passningsalternativ i djupled.',
        'dk': 'Tilbyder sig som en pasningsmulighed i bagrummet.',
      },
    },
  },


  'holdUpScore': {
    name: {
      'default': {
        'en': 'Hold up play',
        'no': 'Oppspillspunkt',
        'se': 'Uppspelspunkt',
        'dk': 'Opholdsspil',
      },
    },
    description: {
      'agf': {
        'en': 'Wall player. Holding up the ball. Ability to receive the ball facing own goal under heavy pressure from opponent defender and keeping possession of the ball.',
        'no': 'Veggspiller. Holder på ballen. Evne til å ta imot ballen med ryggen mot mål under hardt press og beholde ballen.',
        'se': 'Väggspelare. Håller i bollen. Förmåga att ta emot bollen med ryggen mot mål under hård press och behålla bollinnehavet.',
        'dk': 'Vægspiller. Holder på bolden. Evne til at modtage bolden med ryggen mod mål under hårdt pres og fastholde bolden.',
      },
    },
  },

  'linkUpScore': {
    name: {
      'default': {
        'en': 'Link-up play',
        'no': 'Link-up spill',
        'se': 'Link-up spel',
        'dk': 'Link-up spil',
      },
    },
    description: {
      'agf': {
        'en': 'Ability to drop between opponent\'s first and second line to receive the ball in open spaces, reliable first touch and able to operate from there.',
        'no': 'Evne til å falle ned mellom motstanderens første og andre linje for å motta ballen i åpne rom, god førstetouch og i stand til å spille videre derfra.',
        'se': 'Förmåga att falla ner mellan motståndarens första och andra linje för att ta emot bollen i ytor, bra första touch och kan agera därifrån.',
        'dk': 'Evne til at falde ned mellem modstanderens første og anden kæde for at modtage bolden i frie rum, god førstetouch og kan spille videre derfra.',
      },
    },
  },

  'boxPresenceScore': {
    name: {
      'default': {
        'en': 'Box presence',
        'no': 'Boksnærvær',
        'se': 'Boxnärvaro',
        'dk': 'Feltnærvær',
      },
    },
    description: {
      'agf': {
        'en': 'Movements in the box, difficult to mark and getting into finishing positions.',
        'no': 'Bevegelser i boksen, vanskelig å markere og kommer til avslutningsposisjoner.',
        'se': 'Rörelsemönster i straffområdet, svår att markera och tar sig till avslutningslägen.',
        'dk': 'Bevægelser i feltet, svær at dække op og kommer i afslutningspositioner.',
      },
    },
  },

  'boxEntryScore': {
    name: {
      'default': {
        'en': 'Box entry',
        'no': 'Innløp i boks',
        'se': 'Boxinlöp',
        'dk': 'Løb i feltet',
      },
    },
    description: {
      'agf': {
        'en': 'Arriving in the box, actively seeking goal scoring positions.',
        'no': 'Kommer inn i boksen og søker aktivt målsjanser.',
        'se': 'Tar sig in i straffområdet och söker aktivt målchanser.',
        'dk': 'Løber ind i feltet og søger aktivt scoringspositioner.',
      },
    },
  },

  'buildUpScore': {
    name: {
      'default': {
        'en': 'Build up play',
        'no': 'Oppbyggende spill',
        'se': 'Uppbyggnadsspel',
        'dk': 'Opbygningsspil',
      },
    },
    description: {
      'agf': {
        'en': 'Actively involved in the build-up phase, finding progressive solutions to help his team going forward.',
        'no': 'Aktivt involvert i oppbyggingsfasen, finner fremoverrettede løsninger for å hjelpe laget fremover.',
        'se': 'Aktivt involverad i uppbyggnadsfasen, hittar progressiva lösningar för att hjälpa laget framåt.',
        'dk': 'Aktivt involveret i opbygningsfasen, finder fremadrettede løsninger for at hjælpe holdet fremad.',
      },
    },
  },


  // defending
  'defendingScore': {
    name: {
      'default': {
        'en': 'Defending',
        'no': 'Forsvar',
        'se': 'Försvar',
        'dk': 'Forsvar',
      },
    },
  },

  '1v1DefensiveScore': {
    name: {
      'default': {
        'en': 'Defensive 1v1',
        'no': 'Defensiv 1v1',
        'se': 'Defensiv 1v1',
        'dk': 'Defensiv 1v1',
      },
    },
    description: {
      'agf': {
        'en': 'Facing an attacking player running at speed against the defender.',
        'no': 'Møte en angriper i fart én mot én.',
        'se': 'Möta en anfallare i hög fart en mot en.',
        'dk': 'Stå overfor en angriber i høj fart én mod én.',
      },
    },
  },

  'boxDefendingScore': {
    name: {
      'default': {
        'en': 'Box defending',
        'no': 'Boksforsvar',
        'se': 'Boxförsvar',
        'dk': 'Feltforsvar',
      },
    },
    description: {
      'agf': {
        'en': 'Box positioning, covering danger zones.',
        'no': 'Posisjonering i boksen, dekke farlige soner.',
        'se': 'Positionering i straffområdet, täcka farliga ytor.',
        'dk': 'Positionering i feltet, dække farlige zoner.',
      },
    },
  },

  'defensiveUnderstandingScore': {
    name: {
      'default': {
        'en': 'Defensive understanding',
        'no': 'Defensiv forståelse',
        'se': 'Defensiv förståelse',
        'dk': 'Defensiv forståelse',
      },
    },
    description: {
      'agf': {
        'en': 'Positioning, tracking of runs and generel awareness.',
        'no': 'Posisjonering, løpsoppfangelse og generell årvåkenhet.',
        'se': 'Positionering, uppföljning av löpningar och generell speluppfattning.',
        'dk': 'Positionering, opfølgning af løb og generel spilforståelse.',
      },
    },
  },

  'defensivePositioningScore': {
    name: {
      'default': {
        'en': 'Defensive positioning',
        'no': 'Defensiv posisjonering',
        'se': 'Defensiv positionering',
        'dk': 'Defensiv positionering',
      },
    },
    description: {
      'agf': {
        'en': 'Positioning himself well to prepare for counter attacks and possible dangerous situations following turnovers. Covering red-zone.',
        'no': 'Posisjonerer seg godt for å forsvare mot kontringer og farlige situasjoner etter balltap. Dekker røde soner.',
        'se': 'Positionerar sig väl för att hantera kontringar och farliga situationer efter bolltapp. Täckning av hotzoner.',
        'dk': 'Positionerer sig godt for at dæmme op for omstillinger og farlige situationer efter boldtab. Dækker farlige zoner.',
      },
    },
  },

  'forwardDefendingScore': {
    name: {
      'default': {
        'en': 'Forward defending',
        'no': 'Fremoverforsvar',
        'se': 'Offensivt försvar',
        'dk': 'Fremadrettet forsvar',
      },
    },
    description: {
      'agf': {
        'en': 'Aggressive defending pushing forward.',
        'no': 'Aggressivt forsvarsspill som presser høyt fremover.',
        'se': 'Aggressivt försvarsspel som pressar framåt.',
        'dk': 'Aggressivt forsvarsspil med fremadrettet pres.',
      },
    },
  },

  'interceptionScore': {
    name: {
      'default': {
        'en': 'Interception',
        'no': 'Pasningsbrudd',
        'se': 'Passningsbrytning',
        'dk': 'Pasningsopsnapning',
      },
    },
    description: {
      'agf': {
        'en': 'Anticipation to get inbetween passes and quick reactions arriving first to second ball.',
        'no': 'God evne til å forutse og bryte pasninger, og raske reaksjoner for å komme først på andreballer.',
        'se': 'Bra på att läsa spelet för att bryta passningar och snabb i reaktionen för att ta först på andrabollar.',
        'dk': 'God evne til at læse spillet og bryde pasninger samt hurtige reaktioner til at nå først på andenbolde.',
      },
    },
  },


  // duels
  'duellingScore': {
    name: {
      'default': {
        'en': 'Duelling',
        'no': 'Duellspill',
        'se': 'Duellspel',
        'dk': 'Duelspil',
      },
    },
    description: {
      'agf': {
        'en': 'Actively looking for duels and often with success.',
        'no': 'Oppsøker dueller aktivt og vinner dem ofte.',
        'se': 'Söker sig aktivt till dueller och vinner dem ofta.',
        'dk': 'Opsøger aktivt dueller og vinder dem ofte.',
      },
    },
  },

  'duelStrengthScore': {
    name: {
      'default': {
        'en': 'Duel strength',
        'no': 'Duellstyrke',
        'se': 'Duellstyrka',
        'dk': 'Duelstyrke',
      },
    },
    description: {
      'agf': {
        'en': 'Winning positional battles and capable of outmuscling opponents.',
        'no': 'Vinner posisjonsdueller og har evne til å overmanne motstandere fysisk.',
        'se': 'Vinner positionsdueller och har förmåga att övermanna motståndare fysiskt.',
        'dk': 'Vinder positionsdueller og har evnen til fysisk at overmatche modstandere.',
      },
    },
  },

  'aerialScore': {
    name: {
      'default': {
        'en': 'Aerial',
        'no': 'Hodespill',
        'se': 'Huvudspel',
        'dk': 'Hovedspil',
      },
    },
    description: {
      'agf': {
        'en': 'Actively looking for aerial duels and ability to win those.',
        'no': 'Søker aktivt hodedueller og har evne til å vinne dem.',
        'se': 'Söker aktivt luftdueller och har förmåga att vinna dem.',
        'dk': 'Søger aktivt hovedstødsdueller og har evnen til at vinde dem.',
      },
    },
  },


  // set pieces
  'setPieceScore': {
    name: {
      'default': {
        'en': 'Set piece',
        'no': 'Dødball',
        'se': 'Fast situation',
        'dk': 'Standardsituation',
      },
    },
    description: {
      'agf': {
        'en': 'Is the player a weapon on set pieces - either as set piece taker, threat in the box or defensively.',
        'no': 'Er spilleren et våpen på dødballer - enten som dødballtar, trussel i boksen eller defensivt.',
        'se': 'Är spelaren ett vapen vid fasta situationer - som skytt, hot i boxen eller defensivt.',
        'dk': 'Er spilleren et våben ved standardsituationer - enten som sparketager, trussel i feltet eller defensivt.',
      },
    },
  },

  'offensiveSetPieceScore': {
    name: {
      'default': {
        'en': 'Offensive set piece',
        'no': 'Offensiv dødball',
        'se': 'Offensiv fast situation',
        'dk': 'Offensiv standardsituation',
      },
    },
    description: {
      'agf': {
        'en': 'Is the player a threat at offensive set pieces - either as set piece taker or threat in the box.',
        'no': 'Er spilleren en trussel på offensive dødballer - enten som dødballtar eller i boksen.',
        'se': 'Är spelaren ett hot på offensiva fasta situationer - antingen som skytt eller i boxen.',
        'dk': 'Er spilleren en trussel ved offensive standardsituationer - enten som sparketager eller i feltet.',
      },
    },
  },

  'defensiveSetPieceScore': {
    name: {
      'default': {
        'en': 'Defensive set piece',
        'no': 'Defensiv dødball',
        'se': 'Defensiv fast situation',
        'dk': 'Defensiv standardsituation',
      },
    },
    description: {
      'agf': {
        'en': 'Is the player marking important zones or man marking opponent\'s main aerial threats.',
        'no': 'Markerer spilleren viktige soner eller motstanderens største hodeduelltrusler i defensive dødballer.',
        'se': 'Markerar spelaren viktiga zoner eller motståndarens främsta hot i luftrummet vid defensiva fasta situationer.',
        'dk': 'Markerer spilleren vigtige zoner eller modstanderens farligste hovedstødstrusler ved defensive standardsituationer.',
      },
    },
  },


  // goalkeeping
  'reactionScore': {
    name: {
      'default': {
        'en': 'Reflexes',
        'no': 'Reaksjonsevne',
        'se': 'Reaktionstid',
        'dk': 'Reaktionsevne',
      },
    },
  },

  'divingScore': {
    name: {
      'default': {
        'en': 'Diving',
        'no': 'Rekkevidde',
        'se': 'Räckvidd',
        'dk': 'Rækkevidde',
      },
    },
  },
};
